import { PRICE_SYMBOL } from '../const.js'
import type { Options, Product, ProductOption, ProductVariant } from '../types/product'

export function optionsConstructor(options: ProductOption[]): Options {
  return options.reduce((pre: any, cur: any) => {
    pre[cur.name] = cur.values[0].label
    return pre
  }, {})
}

/**
 * Returns the first available variant of a product.
 * If no variant is available, it returns the first variant in the list.
 *
 * @param product - The product object.
 * @returns The first available variant of the product.
 */
export const getFirstAvailableVariant = (product: Product) =>
  product.variants.find((variant: ProductVariant) => variant.availableForSale) || product.variants[0]

export const getVariantBySku = (product: Product, sku: string) =>
  product.variants.find((variant: ProductVariant) => variant.sku === sku)

export const languageMap: { [key: string]: string } = {
  us: 'en-US',
  uk: 'en-GB',
  ca: 'en-CA',
  au: 'en-AU',
  'eu-en': 'en',
  'eu-fr': 'fr-FR',
  'eu-it': 'it-IT',
  'eu-de': 'de-DE',
  'eu-es': 'es-ES',
  'cz-en': 'en-CZ',
  nl: 'nl-NL',
  'dk-en': 'en-DK',
  'gr-en': 'en-GR',
  'mk-en': 'en-MK',
  'hu-en': 'en-HU',
  'se-en': 'en-SE',
  'hr-en': 'en-HR',
  'bg-en': 'en-BG',
  'ee-en': 'en-EE',
  'fi-en': 'en-FI',
  'lt-en': 'en-LT',
  pl: 'pl-PL',
  'ro-en': 'en-RO',
  'si-en': 'en-SI',
  'sk-en': 'en-SK',
  'no-en': 'en-NO',
  'ad-en': 'en-AD',
  'al-en': 'en-AL',
  'ba-en': 'en-BA',
  'me-en': 'en-ME',
  'rs-en': 'en-RS',
  'gl-en': 'en-GL',
  'is-en': 'en-IS',
  'ua-en': 'en-UA',
  'by-en': 'en-BY',
  'md-en': 'en-MD',
  'lv-en': 'en-LV',
  'at-de': 'de-AT',
  'lu-de': 'de-LU',
  'li-de': 'de-LI',
  'va-it': 'it-VA',
  'sm-it': 'it-SM',
  'be-nl': 'nl-BE',
  'ae-en': 'en-AE',
  nz: 'en-NZ',
  'sg-en': 'en-SG',
  'sa-en': 'en-SA',
  'vn-vi': 'vi-VN',
  ie: 'en-IE',
  tw: 'zh-TW',
}

export const languageTerritory = (locale: string) => languageMap[locale] || 'en-US'

export function formatPrice({
  amount,
  currencyCode,
  locale,
  maximumFractionDigits,
  minimumFractionDigits,
  removeTrailingZeros = false,
}: {
  amount: number
  currencyCode: string
  locale: string
  maximumFractionDigits?: number
  minimumFractionDigits?: number
  removeTrailingZeros?: boolean
}) {
  const validCurrencyCode = currencyCode?.trim() || 'USD'
  const validLocale = languageTerritory(locale)

  let formatCurrency: Intl.NumberFormat
  try {
    formatCurrency = new Intl.NumberFormat(validLocale, {
      style: 'currency',
      currency: validCurrencyCode,
      minimumFractionDigits: maximumFractionDigits === 0 ? 0 : minimumFractionDigits,
      maximumFractionDigits,
    })
  } catch {
    // Fallback to en-US with USD if locale/currency combination is invalid
    formatCurrency = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: maximumFractionDigits === 0 ? 0 : minimumFractionDigits,
      maximumFractionDigits,
    })
  }

  let formattedValue = formatCurrency.format(amount)

  if (['eu-de', 'eu-en', 'de', 'eu'].includes(locale)) {
    formattedValue = formattedValue?.toString()?.replace(/[\s]/g, '')
  }

  if (removeTrailingZeros) {
    // Adjust the regex to match comma as decimal separator
    formattedValue = formattedValue
      .replace(/(\.\d*?[1-9])0+\b/, '$1')
      .replace(/\.0+\b/, '')
      // eslint-disable-next-line no-useless-escape
      .replace(/(\,\d*?[1-9])0+\b/, '$1')
      // eslint-disable-next-line no-useless-escape
      .replace(/\,0+\b/, '')
  }

  return formattedValue
}

export function formatVariantPrice({
  amount,
  baseAmount,
  currencyCode,
  locale,
  maximumFractionDigits,
  minimumFractionDigits,
  removeTrailingZeros,
}: {
  baseAmount: number
  amount: number
  currencyCode: string
  locale: string
  maximumFractionDigits?: number
  minimumFractionDigits?: number
  removeTrailingZeros?: boolean
}) {
  if (typeof Intl === 'undefined') {
    return {
      price: `${PRICE_SYMBOL[locale as keyof typeof PRICE_SYMBOL]}${amount}`,
      basePrice: `${PRICE_SYMBOL[locale as keyof typeof PRICE_SYMBOL]}${baseAmount}`,
      discount: `${PRICE_SYMBOL[locale as keyof typeof PRICE_SYMBOL]}${baseAmount - amount || 0}`,
    }
  }

  const hasDiscount = baseAmount > amount
  const formatDiscount = new Intl.NumberFormat(languageTerritory(locale), {
    style: 'percent',
  })
  const discount = hasDiscount ? formatDiscount.format((baseAmount - amount) / baseAmount) : null

  const price = formatPrice({ amount, currencyCode, locale, removeTrailingZeros })
  const basePrice = hasDiscount
    ? formatPrice({
        amount: baseAmount,
        currencyCode,
        locale,
        maximumFractionDigits,
        minimumFractionDigits,
        removeTrailingZeros,
      })
    : undefined

  return { price, basePrice, discount }
}

export function getVariantFromSelectedOptions({
  product,
  selectedOptions,
}: {
  product: Product
  selectedOptions: Record<string, string>
}) {
  return product.variants.find(variant => {
    return Object.entries(selectedOptions).every(([key, value]) =>
      variant.options.find(option => {
        if (option.__typename === 'MultipleChoiceOption' && option.name === key) {
          return option.values.find(v => v.label === value)
        }
      })
    )
  })
}
